import { pageTitle } from '@palaccount/config';
import { CURRENCY_CATALOG, CURRENCY_CODE_RE, type CurrencyRow } from '@palaccount/contracts';
import { Cloud, Pencil, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CurrencyField } from '@/components/ui/currency-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { TableCell } from '@/components/ui/table';
import { useAccounts } from '@/features/accounts/api';
import { AccountingTabs } from '@/features/accounts/accounting-tabs';
import { Modal } from '@/features/inventory/modal';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { isGainOrZero, sumAmounts } from '@/lib/money';
import { localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  useAddCurrency,
  useCurrencyTable,
  useFetchLiveRates,
  usePostRevaluation,
  useRateHistory,
  useRevaluationPreview,
  useRevaluations,
  useSetBaseCurrency,
  useRemoveCurrency,
  useSetCurrencyActive,
  useSetCurrencySource,
  useSetManualRate,
} from './api';
import { RateSparkline } from './rate-sparkline';

/**
 * FIN-05 — Currency Exchange. §4's five stacked cards: the one-time base
 * currency, the rate table, the rate history chart, the revaluation, and
 * the posted history the screen's own §12 leaves behind.
 *
 * The two rules the screen exists to make visible:
 *   - THE direction (§5b): the live example line under the rate box reads
 *     "1.00 USD = 0.9180 EUR" and updates as the number is typed. It "is a
 *     requirement, not decoration — it is the only thing that catches a rate
 *     entered upside down."
 *   - The source (§5c): a Manual rate is editable and survives a fetch; a
 *     Live API rate is grey and read-only FOR EVERYBODY, whatever their
 *     permission, because that is a data-source rule and not an
 *     authorization one. Editing a manual rate stamps "(manual)".
 *
 * §7: zero counts as a GAIN, so a flat revaluation shows the green banner.
 */

const CHIP =
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';

export function CurrenciesPage() {
  const { t } = useTranslation();
  const canEdit = useCan('accounting.edit');
  const canSetBase = useCan('admin.edit');

  const [error, setError] = useState('');
  const [refreshedAt, setRefreshedAt] = useState('');
  const [selectedId, setSelectedId] = useState<string>();
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('fx.title'));
  }, [t]);

  const canDelete = useCan('accounting.delete');
  // TRUE here and nowhere else: this is MD-11's maintenance screen, and §7's
  // Inactive row says what it can do — "Read old documents, switch it back
  // on" — which is impossible on a screen that cannot see it. Every other
  // reader of this hook is a picker and asks without it (BR-DATA-06).
  const { data: table } = useCurrencyTable(true);
  // The pre-load fallback names the company's base from the session rather
  // than assuming dollars (GEN-107); the table itself is the authority once
  // it arrives, and BR-FIN-05-07 keeps the base out of its rows.
  const sessionBase = useBaseCurrency();
  const setSource = useSetCurrencySource();
  const setActive = useSetCurrencyActive();
  const removeCurrency = useRemoveCurrency();
  const setBase = useSetBaseCurrency();
  const fetchRates = useFetchLiveRates();

  const rows = useMemo(() => table?.rows ?? [], [table]);
  // §6 Row click selects the currency the history chart draws.
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0] ?? null;

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own rather
      // than the browser's `Failed to fetch`. Every failure path on this screen
      // names `fx.errSaveFailed`: one screen, one sentence, its own key.
      setError(localizeServerCause(t, cause, 'fx.errSaveFailed'));
    });
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('fx.eyebrow')}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-semibold">{t('fx.title')}</h1>
          <GatedGroup permission="accounting.edit" reasonKey="perm.cannot.refreshRates">
            <div className="flex items-center gap-2">
              {refreshedAt !== '' ? (
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                  {t('fx.refreshedAt', { time: refreshedAt })}
                </span>
              ) : null}
              <Button
                variant="outline"
                onClick={() => {
                  setError('');
                  fetchRates
                    .mutateAsync()
                    .then((result) => {
                      // The REAL time (the fixed-stamp defect, fixed).
                      setRefreshedAt(new Date(result.refreshedAt).toLocaleTimeString());
                    })
                    .catch((cause: unknown) => {
                      setError(localizeServerCause(t, cause, 'fx.errSaveFailed'));
                    });
                }}
              >
                <RefreshCw className="me-1 h-4 w-4" />
                {t('fx.fetchLive')}
              </Button>
            </div>
          </GatedGroup>
        </div>
      </div>
      <AccountingTabs />

      {error !== '' ? (
        <p
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {error}
        </p>
      ) : null}

      {/* ── Card 1: the base currency (§5a) ────────────────────────────── */}
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-semibold">{t('fx.baseTitle')}</h2>
            <p className="max-w-md text-sm text-muted-foreground">{t('fx.baseHint')}</p>
          </div>
          <div className="w-64">
            <Label htmlFor="fx-base">{t('fx.baseLabel')}</Label>
            <NativeSelect
              id="fx-base"
              value={table?.base.code ?? sessionBase}
              // BR-FIN-05-06: once a document exists the stored rates would
              // mean the wrong thing, so the control is dead, not hidden.
              disabled={!canSetBase || (table?.baseLocked ?? true)}
              onChange={(event) => act(setBase.mutateAsync(event.target.value))}
            >
              {CURRENCY_CATALOG.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.code} — {entry.name}
                </option>
              ))}
            </NativeSelect>
            {table?.baseLocked ? (
              <p className="mt-1 text-xs text-muted-foreground">{t('fx.baseLockedHint')}</p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* ── Card 2: the rate table (§5b/§5c) ───────────────────────────── */}
      <Card>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">{t('fx.tableTitle')}</h2>
            {/* Nothing left to add is not a permission answer. */}
            {(table?.addable.length ?? 0) > 0 ? (
              <Gated permission="accounting.create" reasonKey="perm.cannot.addCurrencies">
                <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
                  {t('fx.addCurrency')}
                </Button>
              </Gated>
            ) : null}
          </div>

          {/* §4: a sideways scroller rather than a squashed table. */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="p-2 text-start">{t('fx.colCode')}</th>
                  <th className="p-2 text-start">{t('fx.colCurrency')}</th>
                  <th className="p-2 text-start">{t('fx.colSymbol')}</th>
                  <th className="p-2 text-end">{t('fx.colRate')}</th>
                  <th className="p-2 text-start">{t('fx.colSource')}</th>
                  <th className="p-2 text-start">{t('fx.colUpdated')}</th>
                  <th className="p-2 text-start">{t('fx.colEffective')}</th>
                  {/* MD-11 §6 puts Switch off / Switch on and Delete under a
                      Menu; this screen has no per-row menu, so they are drawn
                      in their own column rather than hidden behind one. */}
                  <th className="p-2 text-start">{t('fx.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <TableCell colSpan={8} className="px-6 text-center text-muted-foreground">
                      {t('fx.emptyTable')}
                    </TableCell>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <RateRow
                      key={row.id}
                      row={row}
                      base={table?.base.code ?? sessionBase}
                      selected={selected?.id === row.id}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onSelect={() => setSelectedId(row.id)}
                      onSource={(source) => act(setSource.mutateAsync({ currencyId: row.id, source }))}
                      onActive={(isActive) =>
                        act(setActive.mutateAsync({ currencyId: row.id, isActive }))
                      }
                      onRemove={() => act(removeCurrency.mutateAsync(row.id))}
                      onError={setError}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ── Card 3: the rate history chart (§4) ────────────────────────── */}
      <HistoryCard currency={selected} />

      {/* ── Card 4: the revaluation (§5d/§5e/§12) ──────────────────────── */}
      <RevaluationCard onError={setError} />

      {/* ── Card 5: what this screen has already posted ────────────────── */}
      <RevaluationHistoryCard />

      {addOpen ? <AddCurrencyModal onClose={() => setAddOpen(false)} onError={setError} /> : null}
    </div>
  );
}

function RateRow({
  row,
  base,
  selected,
  canEdit,
  canDelete,
  onSelect,
  onSource,
  onActive,
  onRemove,
  onError,
}: {
  row: CurrencyRow;
  base: string;
  selected: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onSource: (source: 'manual' | 'live') => void;
  onActive: (isActive: boolean) => void;
  onRemove: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const setRate = useSetManualRate();
  const [draft, setDraft] = useState(row.rate ?? '');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => setDraft(row.rate ?? ''), [row.rate]);

  const live = row.source === 'live';
  const commit = () => {
    if (draft === (row.rate ?? '') || draft.trim() === '') return;
    onError('');
    setRate
      .mutateAsync({ currencyId: row.id, body: { rate: draft.trim() } })
      .catch((cause: unknown) => {
        setDraft(row.rate ?? '');
        onError(localizeServerCause(t, cause, 'fx.errSaveFailed'));
      });
  };

  return (
    /*
     * ERR-FIN-05-06 — "Looked for an instruction that rate rows are
     * clickable: there is none; only the pointer changes." The row documents
     * the prototype's discoverability defect; the behaviour it wants ("Click
     * a row to load its history") is §6's row-click, wired here. This build
     * does not repeat the only-the-pointer half: a click tints the row
     * (§6's pale blue) and retitles the history card to the currency it
     * loaded (`fx.historyFor`), the hover shades as feedback before the
     * click, and the one state with nothing selected says the instruction in
     * words (`fx.pickCurrency`).
     */
    <tr
      onClick={onSelect}
      className={cn('cursor-pointer border-b last:border-0 hover:bg-muted/50', selected && 'bg-blue-50 dark:bg-blue-950/40')}
    >
      <TableCell className="px-2 font-mono font-semibold">
        <Ltr value={row.code} />
      </TableCell>
      <TableCell className="px-2">{row.name}</TableCell>
      <TableCell className="px-2">{row.symbol}</TableCell>
      <TableCell className="px-2 text-end">
        {/* §5c: editable only when the source is Manual — grey and read-only
            on a Live API row, for everybody. */}
        <Input
          aria-label={t('fx.rateFor', { code: row.code })}
          inputMode="decimal"
          className={cn('h-9 w-32 text-end font-mono', live && 'bg-muted text-muted-foreground')}
          readOnly={live || !canEdit}
          disabled={live || !canEdit}
          value={draft}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />
        {/* §5b's required live example line — the only thing that catches a
            rate typed upside down. */}
        <p className="mt-1 text-[11px] text-muted-foreground">
          <Ltr value={`1.00 ${base} = ${draft === '' ? '—' : draft} ${row.code}`} />
        </p>
      </TableCell>
      <TableCell className="px-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              CHIP,
              live
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {live ? <Cloud className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
            {live ? t('fx.sourceLive') : t('fx.sourceManual')}
          </span>
          <GatedGroup permission="accounting.edit" reasonKey="perm.cannot.setRateSource">
            <span className="flex gap-1" onClick={(event) => event.stopPropagation()}>
              <Button
                variant={live ? 'outline' : 'secondary'}
                size="sm"
                onClick={() => onSource('manual')}
              >
                {t('fx.sourceManual')}
              </Button>
              <Button
                variant={live ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => onSource('live')}
              >
                {t('fx.sourceLive')}
              </Button>
            </span>
          </GatedGroup>
        </div>
      </TableCell>
      <TableCell className="px-2 text-xs text-muted-foreground">
        {row.lastUpdatedAt === null ? (
          '—'
        ) : (
          <>
            <Ltr value={row.lastUpdatedAt.slice(0, 10)} />
            {/* BR-FIN-05-05: the difference between "the market says" and
                "someone decided". */}
            {row.lastUpdatedManual ? <span className="ms-1">{t('fx.manualStamp')}</span> : null}
          </>
        )}
      </TableCell>
      <TableCell className="px-2 text-xs text-muted-foreground">
        {row.effectiveDate === null ? '—' : <Ltr value={row.effectiveDate} />}
      </TableCell>
      {/* MD-11 §7's Inactive row — "Grey · Kept for history" — and the two
          actions §6 lists. The base currency never reaches here: BR-FIN-05-07
          keeps it out of `rows` entirely, which is also why no Switch-off
          button has to be hidden for it (BR-MD-11-06 is still refused by the
          server, and ERR-MD-11-08 is what it says). */}
      <TableCell className="px-2">
        <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
          {row.isActive ? null : (
            <span className={cn(CHIP, 'bg-muted text-muted-foreground')}>
              {t('fx.statusInactive')}
            </span>
          )}
          <GatedGroup permission="accounting.edit" reasonKey="perm.cannot.switchOffCurrency">
            <Button variant="outline" size="sm" onClick={() => onActive(!row.isActive)}>
              {row.isActive ? t('fx.switchOff') : t('fx.switchOn')}
            </Button>
          </GatedGroup>
          {canDelete ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setDeleting(true)}>
                {t('common.delete')}
              </Button>
              {/* THIS BUTTON USED TO DELETE A CURRENCY WITH NO QUESTION AT ALL.
                  GEN-58 — "Delete must always ask" — and MD-RULE-3, which says a
                  master record "may be deleted, once, WITH A CONFIRMATION".
                  It was invisible to the `*.confirmDelete` census precisely
                  because it asked nothing, so it had no key to be counted. */}
              <DeleteConfirmBox
                open={deleting}
                detail={t('fx.confirmDelete', { code: row.code })}
                onCancel={() => setDeleting(false)}
                onConfirm={() => {
                  setDeleting(false);
                  onRemove();
                }}
              />
            </>
          ) : null}
        </div>
      </TableCell>
    </tr>
  );
}

function HistoryCard({ currency }: { currency: CurrencyRow | null }) {
  const { t } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data } = useRateHistory(currency?.id ?? null, {
    ...(from !== '' ? { from } : {}),
    ...(to !== '' ? { to } : {}),
  });

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-semibold">
            {currency === null
              ? t('fx.historyTitle')
              : t('fx.historyFor', { code: currency.code, name: currency.name })}
          </h2>
          <div className="flex items-end gap-2">
            <div>
              <Label htmlFor="fx-from">{t('fx.from')}</Label>
              <Input
                id="fx-from"
                type="date"
                className="h-9 w-40"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="fx-to">{t('fx.to')}</Label>
              <Input
                id="fx-to"
                type="date"
                className="h-9 w-40"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
          </div>
        </div>
        {currency === null ? (
          <p className="text-sm text-muted-foreground">{t('fx.pickCurrency')}</p>
        ) : (
          <>
            <RateSparkline points={data?.points ?? []} />
            {/*
              MD-RULE-5's showing half — the four facts of the CURRENCY
              RECORD itself (MD-11), on the selected currency's own card.
              These are not the rate's stamps: the table's "Last updated"
              column is the current RATE row's, and it moves on every fetch
              while the record sits unchanged. `AuthorshipLine` is the
              shared component, so a NULL author renders through the
              decision-28 seam like every other master kind.
            */}
            <AuthorshipLine record={currency} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RevaluationCard({ onError }: { onError: (message: string) => void }) {
  const { t } = useTranslation();
  const [date, setDate] = useDocumentDate();
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [gainLossAccountId, setGainLossAccountId] = useState('');

  const { data: preview } = useRevaluationPreview(date);
  const { data: accounts } = useAccounts();
  const post = usePostRevaluation();

  const rows = preview?.rows ?? [];
  // Every derived row starts ticked; unticking leaves it on screen at half
  // opacity, contributing nothing (§6).
  const isTicked = (accountId: string) => ticked[accountId] ?? true;
  const selectedIds = rows.filter((row) => isTicked(row.accountId)).map((row) => row.accountId);
  const total = sumAmounts(
    rows.filter((row) => isTicked(row.accountId)).map((row) => row.gainLoss),
  );
  // §7: zero counts as a gain.
  const isGain = isGainOrZero(total);

  // BR-FIN-05-11: only revenue and expense accounts are offered — the gain
  // or loss must reach the profit and loss, never the balance sheet.
  const plAccounts = (accounts ?? []).filter(
    (account) => account.postable && account.active && (account.type === 'revenue' || account.type === 'expense'),
  );

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-semibold">{t('fx.revalTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('fx.revalHint')}</p>
          </div>
          <div>
            <Label htmlFor="fx-reval-date">{t('fx.revalDate')}</Label>
            <Input
              id="fx-reval-date"
              type="date"
              className="h-9 w-44"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="p-2 text-start">{t('fx.colAccount')}</th>
                <th className="p-2 text-end">{t('fx.colOriginalFc')}</th>
                <th className="p-2 text-end">{t('fx.colRateUsed')}</th>
                <th className="p-2 text-end">{t('fx.colCurrentRate')}</th>
                <th className="p-2 text-end">{t('fx.colRevalued')}</th>
                <th className="p-2 text-end">{t('fx.colGainLoss')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <TableCell colSpan={6} className="px-6 text-center text-muted-foreground">
                    {t('fx.emptyReval')}
                  </TableCell>
                </tr>
              ) : (
                rows.map((row) => {
                  const on = isTicked(row.accountId);
                  const gain = row.gainLoss;
                  return (
                    <tr
                      key={row.accountId}
                      className={cn('border-b last:border-0', !on && 'opacity-50')}
                    >
                      <TableCell className="px-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={on}
                            aria-label={t('fx.tickAccount', { code: row.code })}
                            onChange={(event) =>
                              setTicked((current) => ({
                                ...current,
                                [row.accountId]: event.target.checked,
                              }))
                            }
                          />
                          <span>
                            <span className="font-medium">{row.name}</span>
                            <span className="ms-2 font-mono text-xs text-muted-foreground">
                              <Ltr value={row.code} />
                            </span>
                            <span className="ms-2 text-xs text-muted-foreground">
                              {row.kind === 'asset' ? t('fx.kindAsset') : t('fx.kindLiability')}
                            </span>
                          </span>
                        </label>
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={row.fcBalance} currency={row.currency} />
                      </TableCell>
                      <TableCell className="px-2 text-end font-mono">
                        <Ltr value={row.rateUsed} />
                      </TableCell>
                      <TableCell className="px-2 text-end font-mono font-semibold text-blue-600 dark:text-blue-400">
                        <Ltr value={row.currentRate} />
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={row.revaluedBase} />
                      </TableCell>
                      <TableCell
                        className={cn(
                          'px-2 text-end font-semibold',
                          isGainOrZero(gain) ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
                        )}
                      >
                        {isGainOrZero(gain) ? '+' : ''}
                        <Amount value={row.gainLoss} />
                      </TableCell>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* §15 Partial: an account that cannot be revalued says why. */}
        {(preview?.skipped.length ?? 0) > 0 ? (
          <ul className="space-y-1 text-xs text-muted-foreground">
            {preview!.skipped.map((row) => (
              <li key={row.accountId}>
                <Ltr value={row.code} /> — {row.reason}
              </li>
            ))}
          </ul>
        ) : null}

        <div
          className={cn(
            'flex items-center justify-between rounded-md border px-3 py-2 font-semibold',
            isGain
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
              : 'border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300',
          )}
        >
          <span className="flex items-center gap-2">
            {isGain ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
            {isGain ? t('fx.totalGain') : t('fx.totalLoss')}
          </span>
          <span>
            {isGain ? '+' : ''}
            <Amount value={total} />
          </span>
        </div>

        <GatedGroup permission="accounting.approve" reasonKey="perm.cannot.postRevaluation">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="w-72">
              <Label htmlFor="fx-gain-loss">{t('fx.postTo')}</Label>
              <NativeSelect
                id="fx-gain-loss"
                value={gainLossAccountId}
                onChange={(event) => setGainLossAccountId(event.target.value)}
              >
                <option value="">{t('common.choose')}</option>
                {plAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.code} — {account.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <Button
              // §6: greyed out unless at least one account is ticked.
              disabled={selectedIds.length === 0 || gainLossAccountId === '' || post.isPending}
              onClick={() => {
                onError('');
                post
                  .mutateAsync({ date, accountIds: selectedIds, gainLossAccountId })
                  .catch((cause: unknown) => {
                    onError(localizeServerCause(t, cause, 'fx.errSaveFailed'));
                  });
              }}
            >
              {t('fx.postReval')}
            </Button>
          </div>
        </GatedGroup>
        <p className="text-xs text-muted-foreground">{t('fx.postingHint')}</p>
      </CardContent>
    </Card>
  );
}

function RevaluationHistoryCard() {
  const { t } = useTranslation();
  /** NFR-42 — the posted history pages; the page rides in the URL. */
  const paging = usePagedState();
  const { data } = useRevaluations(paging.page, paging.pageSize);
  const revaluations = data?.revaluations ?? [];
  const range = usePageRange(data, paging.pageSize);

  return (
    <Card>
      <CardContent className="space-y-2">
        <h2 className="font-semibold">{t('fx.postedTitle')}</h2>
        {revaluations.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('fx.emptyPosted')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="p-2 text-start">{t('fx.colRun')}</th>
                <th className="p-2 text-start">{t('common.date')}</th>
                <th className="p-2 text-start">{t('fx.colPostedTo')}</th>
                <th className="p-2 text-end">{t('fx.colGainLoss')}</th>
              </tr>
            </thead>
            <tbody>
              {revaluations.map((revaluation) => (
                <tr key={revaluation.id} className="border-b last:border-0">
                  <TableCell className="px-2 font-mono">
                    <Ltr value={revaluation.code} />
                  </TableCell>
                  <TableCell className="px-2">
                    <Ltr value={revaluation.revaluationDate} />
                  </TableCell>
                  <TableCell className="px-2">{revaluation.gainLossAccountName}</TableCell>
                  <TableCell
                    className={cn(
                      'px-2 text-end font-semibold',
                      isGainOrZero(revaluation.total) ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
                    )}
                  >
                    {isGainOrZero(revaluation.total) ? '+' : ''}
                    <Amount value={revaluation.total} />
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* NFR-42 — the range, the total, and the two steps. */}
        {(data?.total ?? 0) > 0 ? (
          <Pager
            page={paging.page}
            pages={range.pages}
            first={range.first}
            last={range.last}
            total={range.total}
            onPage={paging.setPage}
            label={t('fx.postedTitle')}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * D16 — THE BOX THAT WAS A FENCE.
 *
 * The code field was a `<NativeSelect>` over `table.addable`, so the seven in
 * `CURRENCY_CATALOG` were the only currencies that could ever exist. The owner
 * ruled the list OPEN, so it is now `<CurrencyField>` — free text with those
 * same seven offered underneath — and the seven changed job from FENCE to
 * SUGGESTION without changing content.
 *
 * `<CurrencyField>` is the reuse rather than a new control because its whole
 * design note is this case: "the VALUE HERE IS THE TEXT: the suggestions are
 * help, not a gate", written for the two free-text screens that already needed
 * a code the tenant has not added yet to be typeable. This screen is where
 * such a code is added, so it needed it most and had it least.
 *
 * TWO SENTENCES GUARD THE BOX, and they are different questions in the order a
 * user meets them — shape, then existence:
 *   `fx.errCodeShape`  ERR-MD-11-01, BEFORE the post. The spec's own sentence,
 *                      finding its first site in the product; see
 *                      `FX_MESSAGES.codeShape`.
 *   `fx.errDuplicate`  is answered by the server (ERR-MD-11-02), because only
 *                      it knows what the tenant already holds.
 *
 * THE NAME BOX IS NEW AND IS NOT DECORATION. MD-11 field 2 makes Name required,
 * and there is NO rename route — BR-MD-11-02 locks the code and nothing writes
 * `name` after the insert — so whatever a currency is created with, it keeps
 * for good. While the list was closed this never showed: every creatable code
 * had a catalogue name behind it. Open the list and a typed `JPY` would have
 * been stored, permanently, as "JPY".
 */
function AddCurrencyModal({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  // The same cache entry the page holds. `addable` is computed server-side
  // from EVERY row whatever the filter, so a switched-off EUR is absent from
  // this list and the answer is Switch on rather than Add.
  const { data: table } = useCurrencyTable(true);
  // The pre-load fallback names the company's base from the session rather
  // than assuming dollars (GEN-107); the table itself is the authority once
  // it arrives, and BR-FIN-05-07 keeps the base out of its rows.
  const sessionBase = useBaseCurrency();
  const add = useAddCurrency();
  const addable = table?.addable ?? [];
  /**
   * EMPTY, not `addable[0]`. The old select had to start on a valid option
   * because every option was valid; a free-text box that arrives pre-filled
   * with a currency nobody asked for is how a company ends up holding a stray
   * `AED` it cannot delete once a document names it.
   */
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [rate, setRate] = useState('');
  const [shapeError, setShapeError] = useState(false);
  const base = table?.base.code ?? sessionBase;
  const trimmedCode = code.trim().toUpperCase();

  /**
   * The catalogue's remaining job: typing one of the seven fills its name in,
   * so adding EUR is still one keystroke's worth of work and still produces
   * "Euro" rather than "EUR". It stops the moment the user edits the name —
   * the same `touched` guard the bank-account currency box uses — because
   * overwriting what somebody typed is worse than not helping.
   */
  useEffect(() => {
    if (nameTouched) return;
    setName(CURRENCY_CATALOG.find((entry) => entry.code === trimmedCode)?.name ?? '');
  }, [trimmedCode, nameTouched]);

  return (
    <Modal title={t('fx.addCurrency')} onClose={onClose} className="max-w-md">
      <div className="space-y-3">
        <div>
          <Label htmlFor="fx-new-code">{t('fx.colCode')}</Label>
          <CurrencyField
            id="fx-new-code"
            label={t('fx.colCode')}
            value={code}
            onChange={(next) => {
              setShapeError(false);
              setCode(next.toUpperCase());
            }}
            options={addable}
          />
          <p className="mt-1 text-xs text-muted-foreground">{t('fx.codeHint')}</p>
          {/* ERR-MD-11-01, before the post — D17's principle applied to the
              screen where codes are typed. Shown only after a rejected press,
              so it does not scold on the way to a correct three-letter code. */}
          {shapeError ? (
            <p className="mt-1 text-xs text-destructive">{t('fx.errCodeShape')}</p>
          ) : null}
        </div>
        <div>
          <Label htmlFor="fx-new-name">{t('fx.fName')}</Label>
          <Input
            id="fx-new-name"
            maxLength={60}
            value={name}
            onChange={(event) => {
              setNameTouched(true);
              setName(event.target.value);
            }}
          />
        </div>
        <div>
          <Label htmlFor="fx-new-rate">{t('fx.colRate')}</Label>
          <Input
            id="fx-new-rate"
            inputMode="decimal"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            <Ltr
              value={`1.00 ${base} = ${rate === '' ? '—' : rate} ${trimmedCode === '' ? '—' : trimmedCode}`}
            />
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={code.trim() === '' || add.isPending}
            onClick={() => {
              onError('');
              // The same regex the server and the database both hold
              // (`CURRENCY_CODE_RE`). A warning here, never a block: the button
              // stays enabled and the server remains the judge, exactly as the
              // two other free-text currency boxes work.
              if (!CURRENCY_CODE_RE.test(trimmedCode)) {
                setShapeError(true);
                return;
              }
              setShapeError(false);
              add
                .mutateAsync({
                  code: trimmedCode,
                  // Absent rather than empty, so the server's own fallback
                  // chain (typed → catalogue → the code) stays the one place
                  // that decides what an unnamed currency is called.
                  ...(name.trim() !== '' ? { name: name.trim() } : {}),
                  ...(rate.trim() !== '' ? { rate: rate.trim() } : {}),
                })
                .then(onClose)
                .catch((cause: unknown) => {
                  onError(localizeServerCause(t, cause, 'fx.errSaveFailed'));
                });
            }}
          >
            {t('common.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
